import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Empty } from "@/components/UI";
import { toast } from "sonner";

const empty = { style_name: "", sku: "", size: "", color: "", description: "", hsn: "", reorder_level: 0, pieces_per_box: 0 };

export default function ProductsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setF({
      style_name: p.style_name, sku: p.sku, size: p.size, color: p.color,
      description: p.description || "", hsn: p.hsn || "", reorder_level: p.reorder_level || 0,
      pieces_per_box: p.pieces_per_box || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, reorder_level: parseInt(f.reorder_level || 0), pieces_per_box: parseInt(f.pieces_per_box || 0) };
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader label="Master" title="Products / Styles" subtitle="Style + SKU + Size + Color" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={editing ? "Edit Product" : "Add Product"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="product-form">
            <Field label="Style Name"><Input data-testid="product-style" required value={f.style_name} onChange={(e) => setF({ ...f, style_name: e.target.value })} /></Field>
            <Field label="SKU"><Input data-testid="product-sku" required value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></Field>
            <Field label="Size"><Input data-testid="product-size" required value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} placeholder="S / M / L / XL" /></Field>
            <Field label="Color"><Input data-testid="product-color" required value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
            <Field label="Description"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
            <Field label="HSN Code (job-work compliance)">
              <Input data-testid="product-hsn" placeholder="e.g. 6109 for inner garments" value={f.hsn} onChange={(e) => setF({ ...f, hsn: e.target.value.replace(/[^\d]/g, "").slice(0, 8) })} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Re-order Level (Pcs)"><Input type="number" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} placeholder="0" /></Field>
              <Field label="Pcs / Box"><Input type="number" value={f.pieces_per_box} onChange={(e) => setF({ ...f, pieces_per_box: e.target.value })} placeholder="0" /></Field>
            </div>
            <Btn data-testid="product-submit">{editing ? "Save Changes" : "Add Product"}</Btn>
          </form>
        </Card>
        <Card title="Products List" className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Style</th><th>SKU</th><th>Size</th><th>Color</th><th>Desc</th><th className="text-right">Re-order</th><th className="text-right">Pcs/Box</th><th></th></tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className={editing === p.id ? "bg-secondary" : ""} data-testid={`product-row-${p.sku}`}>
                    <td>{p.style_name}</td>
                    <td className="font-mono">{p.sku}</td>
                    <td>{p.size}</td>
                    <td>{p.color}</td>
                    <td className="text-muted-foreground">{p.description}</td>
                    <td className="tabular text-right">{p.reorder_level || 0}</td>
                    <td className="tabular text-right">{p.pieces_per_box || 0}</td>
                    <td className="text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(p)} className="label-caps hover:text-foreground mr-3" data-testid={`product-edit-${p.sku}`}>Edit</button>
                          <button onClick={() => del(p.id)} className="label-caps hover:text-destructive">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={8}><Empty text="No products yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
